import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useState } from 'react'

import { countriesCurrencies } from '../lib'
import { useSocket } from '../hooks/use-socket'
import { resolveAppAccessToken } from '../sso/services/app-access.service'
import '../styles/banks-balances-brief-management.css'
import { UserContext } from '../contexts/user.context'
import { bankingPartnerLogos } from '../lib/common.lib'
import { AppStateContext } from '../contexts/app-state.context'
import { getBankBalancesBriefData } from '../api/app-queries.api'
import { UpdateBankBalanceModal } from '../components/clients-balances-management/update-bank-balance-modal'
import { numberWithCommas, canDisplayTotalBalanceBrief, canDisplayBalancesFromBrankingPartners } from '../utils'
import { BankBalanceSquareContainer } from '../components/banks-balances-management/bank-balance-square-container'
import { ClientAvailableBalanceBrief } from '../components/clients-balances-management/client-available-balance-brief'

const BanksBalancesBriefManagement = () => {
  const { t } = useTranslation(['banks-balances-management'])

  const { userSession } = useContext(UserContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { banksBalancesDataLoaded, bankingPartnersBalanceByCountry } = useContext(AppStateContext)

  const [currency, setCurrency] = useState<string>('COP')
  const [dataIsLoading, setDataIsLoading] = useState<boolean>(true)
  const [selectedBankName, setSelectedBankName] = useState<string>('')
  const [balanceToBeDebited, setBalanceToBeDebited] = useState<number>(0)
  const [balanceToBeCredited, setBalanceToBeCredited] = useState<number>(0)
  const [totalAvailableBalance, setTotalAvailableBalance] = useState<number>(0)
  const [openUpdateBalanceModal, setOpenUpdateBalanceModal] = useState<boolean>(false)
  const [displayTotalBalanceBrief, setDisplayTotalBalanceBrief] = useState<boolean>(false)
  const [displayBankingPartnersData, setDisplayBankingPartnersData] = useState<boolean>(false)

  useEffect(() => {
    if (!appDataSocket) return

    appDataSocket.on('updateSystemUser', async (dataFromSocket: any) => {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

      if (String(userSession?._id) === String(dataFromSocket.systemUserId)) {
        handleGetBankBalancesBriefDataFromRemote(selectedCountry)
      }
    })

    appDataSocket.on('addCustomerCompanyPocket', async (dataFromSocket: any) => {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (selectedCurrency === dataFromSocket.currency) {
        handleGetBankBalancesBriefDataFromRemote(selectedCountry)
      }
    })

    appDataSocket.on('updateCustomerCompanyPocket', async (dataFromSocket: any) => {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (selectedCurrency === dataFromSocket.currency) {
        handleGetBankBalancesBriefDataFromRemote(selectedCountry)
      }
    })

    appDataSocket.on('deleteCustomerCompanyPocket', async (dataFromSocket: any) => {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (selectedCurrency === dataFromSocket.currency) {
        handleGetBankBalancesBriefDataFromRemote(selectedCountry)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    if (userSession) {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

      setDisplayTotalBalanceBrief(canDisplayTotalBalanceBrief(userSession))
      setDisplayTotalBalanceBrief(false)
      setCurrency((countriesCurrencies as { [key: string]: string })[selectedCountry])
      setDisplayBankingPartnersData(canDisplayBalancesFromBrankingPartners(userSession))
      handleGetBankBalancesBriefData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  const handleGetBankBalancesBriefData = async () => {
    let localBankBalancesBriefDataToSet: any = {}
    const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
    const localBankBalancesBriefData = globalThis.sessionStorage.getItem(`bankBalancesBriefData:${selectedCountry}`)

    if (localBankBalancesBriefData) {
      localBankBalancesBriefDataToSet = JSON.parse(localBankBalancesBriefData)
      handleSetInitialBankBalancesBriefData(localBankBalancesBriefDataToSet)
    }

    handleGetBankBalancesBriefDataFromRemote(selectedCountry)
  }

  const handleGetBankBalancesBriefDataFromRemote = async (selectedCountry: string) => {
    try {
      const accessToken = await resolveAppAccessToken()
      const { data } = await getBankBalancesBriefData(String(userSession?._id), selectedCountry, accessToken)

      globalThis.sessionStorage.setItem(`bankBalancesBriefData:${selectedCountry}`, JSON.stringify(data))
      handleSetInitialBankBalancesBriefData(data)
    } catch (error: any) {
      console.log('Error handleGetBankBalancesBriefDataFromRemote > ', error)
    }
  }

  const handleSetInitialBankBalancesBriefData = (dataToSet: any) => {
    setTotalAvailableBalance(dataToSet.balanceBriefData.totalBalance)
    setBalanceToBeDebited(dataToSet.balanceBriefData.balanceToBeDebited)
    setBalanceToBeCredited(dataToSet.balanceBriefData.balanceToBeCredited)
    setDataIsLoading(false)
  }

  return (
    <div className={displayBankingPartnersData ? 'bank-balances-management-content' : 'bank-balances-management-content bank-balances-management-client-content'}>
      {displayTotalBalanceBrief && (
        <div className="total-available-balance-title-container-mobile">
          <div className="total-available-balance-title-container">
            <h1 className="banks-available-balances-title">{`${t('briefData.titleTotalBalanceAvailable')}:`}</h1>
            <div className="banks-available-balances-details-container">
              <div>{`$ ${numberWithCommas(totalAvailableBalance ?? 0)} ${currency}`}</div>
            </div>
          </div>
        </div>
      )}
      <div
        className="bank-balances-container"
        style={userSession?.role?.includes('-client') ? { minHeight: 'unset' } : {}}
      >
        {displayBankingPartnersData ? (
          <>
            {displayTotalBalanceBrief ? (
              <div className="banks-balances-title-container">
                <h1 className="banks-balances-title">{t('briefData.title')}</h1>
                <div className="total-available-balance-title-container">
                  <h1 className="banks-available-balances-title">{`${t('briefData.titleTotalBalanceAvailable')}:`}</h1>
                  <div className="banks-available-balances-details-container">
                    <div>{`$ ${numberWithCommas(totalAvailableBalance ?? 0)} ${currency}`}</div>
                  </div>
                </div>
              </div>
            ) : (
              <h1 className="banks-balances-title">{t('briefData.title')}</h1>
            )}
            <div className="banks-balances-squares-containers">
              {banksBalancesDataLoaded ? (
                <>
                  {bankingPartnersBalanceByCountry?.map((bankingPartnerData) => (
                    <BankBalanceSquareContainer
                      hideSecondaryData
                      key={bankingPartnerData.name}
                      dataLoaded={banksBalancesDataLoaded}
                      logoImage={(bankingPartnerLogos as { [key: string]: string })[bankingPartnerData.bdValue]}
                      mainData={`$ ${numberWithCommas(Number(bankingPartnerData.balance))} ${currency}`}
                    />
                  ))}
                </>
              ) : (
                <div
                  className="data-container-loader"
                  style={{ padding: '12px', minHeight: '140px', width: '100%', alignSelf: 'center' }}
                ></div>
              )}
            </div>
            {openUpdateBalanceModal && (
              <UpdateBankBalanceModal
                selectedBankName={selectedBankName}
                handleCloseOptionAction={() => {
                  setSelectedBankName('')
                  setOpenUpdateBalanceModal(false)
                }}
                handleDialogOpenState={(openState: boolean) => {
                  setSelectedBankName('')
                  setOpenUpdateBalanceModal(openState)
                }}
              />
            )}
          </>
        ) : (
          <ClientAvailableBalanceBrief
            currency={currency}
            dataLoaded={!dataIsLoading}
            balanceToBeDebited={balanceToBeDebited}
            balanceToBeCredited={balanceToBeCredited}
            totalAvailableBalance={totalAvailableBalance}
          />
        )}
      </div>
    </div>
  )
}

export default BanksBalancesBriefManagement
